import React, { useState, useMemo, memo, useEffect, useRef, useLayoutEffect } from 'react';
// Note: useState and useLayoutEffect are also used in TradeRow component
import { useTranslation } from 'react-i18next';
import { formatTimestamp } from '../../utils/helpers';
import './PolymarketTradesTable.css';

// Format dollar amount
const formatDollarAmount = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
};

// Format time ago with translation
const formatTimeAgo = (timestamp, t) => {
  if (!timestamp) return '';

  const now = Date.now();
  const time = timestamp instanceof Date ? timestamp.getTime() : new Date(timestamp).getTime();
  const diffMs = now - time;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return t('time.secondsAgo', { count: diffSec });
  if (diffSec < 3600) return t('time.minutesAgo', { count: Math.floor(diffSec / 60) });
  if (diffSec < 86400) return t('time.hoursAgo', { count: Math.floor(diffSec / 3600) });
  return t('time.daysAgo', { count: Math.floor(diffSec / 86400) });
};

// Volume filter options (values only, labels are generated with translation)
const VOLUME_VALUES = [5000, 10000, 50000, 100000, 500000];

const TradeRow = memo(({ trade, onClick, timeAgo, isLargestTrade, t }) => {
  const titleRef = useRef(null);
  const containerRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  // Determine display side - use outcome from API, not side (BUY/SELL)
  // trade.outcome contains the actual bet outcome (Yes, No, Kings, etc.)
  // trade.side just indicates transaction direction (BUY/SELL), not what they're betting on
  const outcomeText = trade.outcome || 'Yes';
  const isYes = outcomeText.toUpperCase() === 'YES';

  const getDisplaySide = () => {
    // For named outcomes (not Yes/No), show the outcome text
    if (trade.outcome && !['YES', 'NO'].includes(trade.outcome.toUpperCase())) {
      // For short outcomes (like ">$2B"), show full text
      // For longer outcomes (team names etc), truncate to 5 chars + ellipsis
      if (trade.outcome.length <= 6) {
        return trade.outcome;
      }
      return trade.outcome.substring(0, 5) + '…';
    }
    // For Yes/No markets, show the actual outcome
    return outcomeText.toUpperCase() === 'YES' ? 'YES' : 'NO';
  };
  const displaySide = getDisplaySide();

  // Use eventTitle if available, fallback to marketTitle
  const mainTitle = trade.eventTitle || trade.marketTitle || trade.marketSlug;

  // Check if title overflows container (needs scrolling)
  useLayoutEffect(() => {
    if (titleRef.current && containerRef.current) {
      const textWidth = titleRef.current.scrollWidth;
      const containerWidth = containerRef.current.clientWidth;
      setNeedsScroll(textWidth > containerWidth);
    }
  }, [mainTitle]);

  return (
    <div
      className={`whale-trade-row ${isLargestTrade ? 'largest-trade' : ''}`}
      onClick={() => onClick(trade)}
    >
      {/* Largest Trade Icon */}
      {isLargestTrade && (
        <div className="whale-icon">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
          </svg>
        </div>
      )}

      {/* Market Info */}
      <div className="trade-market-col">
        {trade.marketIcon && (
          <img
            className="market-icon"
            src={trade.marketIcon}
            alt=""
          />
        )}
        <div className="market-details">
          <div className="event-title-container" ref={containerRef} title={mainTitle}>
            <span
              ref={titleRef}
              className={`event-title-text ${needsScroll ? 'scrolling' : ''}`}
              data-text={needsScroll ? mainTitle : undefined}
            >
              {mainTitle}
            </span>
          </div>
          {(trade.marketOutcome || trade.price) && (
            <span className="market-outcome-label">
              {trade.marketOutcome}
              {trade.price && ` ${t('polymarket.atPrice', { price: Math.round(trade.price * 100) })}`}
            </span>
          )}
          {isLargestTrade ? (
            <span className="whale-alert-label">{t('polymarket.largestTrade')}</span>
          ) : (
            <span className="trade-time">{timeAgo}</span>
          )}
        </div>
      </div>

      {/* Size */}
      <div className={`trade-size-col ${isLargestTrade ? 'mega' : ''}`}>
        {formatDollarAmount(trade.dollarSize)}
      </div>

      {/* Side Badge */}
      <div className="trade-side-col">
        <span className={`side-badge ${trade.outcome && !['YES', 'NO'].includes(trade.outcome.toUpperCase()) ? 'neutral' : (isYes ? 'yes' : 'no')}`}>
          {displaySide}
        </span>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.trade.transactionHash === nextProps.trade.transactionHash &&
    prevProps.trade.timestamp === nextProps.trade.timestamp &&
    prevProps.timeAgo === nextProps.timeAgo &&
    prevProps.isLargestTrade === nextProps.isLargestTrade
  );
});

TradeRow.displayName = 'TradeRow';

function PolymarketTradesTable({ trades, loading, onTradeClick }) {
  const { t } = useTranslation();
  const [minVolume, setMinVolume] = useState(5000);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Scroll management
  const scrollContainerRef = useRef(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const prevTradesLengthRef = useRef(trades.length);
  const scrollPositionRef = useRef(0);

  // Update current time every second for time ago display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Track scroll position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atTop = container.scrollTop < 50;
      setIsAtTop(atTop);
      scrollPositionRef.current = container.scrollTop;
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [trades.length]);

  // Preserve scroll position when new trades are added
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const newTradesCount = trades.length - prevTradesLengthRef.current;

    if (newTradesCount > 0 && !isAtTop) {
      const firstRow = container.querySelector('.whale-trade-row');
      const rowHeight = firstRow ? firstRow.offsetHeight : 70;
      const scrollAdjustment = newTradesCount * rowHeight;

      container.scrollTop = scrollPositionRef.current + scrollAdjustment;
      scrollPositionRef.current = container.scrollTop;
    }

    prevTradesLengthRef.current = trades.length;
  }, [trades, isAtTop]);

  // Scroll to top function
  const scrollToTop = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      setIsAtTop(true);
    }
  };

  // Filter trades by volume only
  const filteredTrades = useMemo(() => {
    return trades.filter(trade => trade.dollarSize >= minVolume);
  }, [trades, minVolume]);

  // Find largest trade (for highlighting)
  const largestTradeKey = useMemo(() => {
    if (filteredTrades.length === 0) return null;
    const largest = filteredTrades.reduce((max, trade) =>
      trade.dollarSize > max.dollarSize ? trade : max
    );
    return `${largest.transactionHash || largest.marketSlug}-${largest.timestamp}`;
  }, [filteredTrades]);

  // Handle trade click
  const handleTradeClick = (trade) => {
    if (onTradeClick) {
      onTradeClick(trade);
    }
  };

  // Calculate time since last trade for header
  const lastUpdateAgo = useMemo(() => {
    if (!filteredTrades || filteredTrades.length === 0) return t('polymarket.noTrades');
    const newestTrade = filteredTrades[0];
    const tradeTime = newestTrade.timestamp instanceof Date
      ? newestTrade.timestamp.getTime()
      : new Date(newestTrade.timestamp).getTime();
    const diffSec = Math.floor((currentTime.getTime() - tradeTime) / 1000);
    if (diffSec < 60) return t('time.secondsAgo', { count: diffSec });
    if (diffSec < 3600) return t('time.minutesAgo', { count: Math.floor(diffSec / 60) });
    return t('time.hoursAgo', { count: Math.floor(diffSec / 3600) });
  }, [filteredTrades, currentTime, t]);

  if (loading) {
    return (
      <div className="card whale-trades-card">
        <div className="whale-trades-header">
          <h3 className="whale-trades-title">{t('polymarket.whaleTrades')}</h3>
        </div>
        <div className="whale-trades-body">
          <div className="loading-state">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card whale-trades-card">
      {/* Header with Volume Filter */}
      <div className="whale-trades-header">
        <h3 className="whale-trades-title">{t('polymarket.whaleTrades')}</h3>
        <div className="header-right">
          <div className="header-update-time">
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
            </svg>
            <span>{lastUpdateAgo}</span>
          </div>
          <select
            className="polymarket-filter-dropdown compact"
            value={minVolume}
            onChange={(e) => setMinVolume(Number(e.target.value))}
          >
            {VOLUME_VALUES.map(value => (
              <option key={value} value={value}>
                Vol &gt; ${value >= 1000 ? `${value / 1000}k` : value}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Column Headers */}
      <div className="whale-trades-columns">
        <div className="col-market">{t('polymarket.market')}</div>
        <div className="col-size">{t('polymarket.size')}</div>
        <div className="col-side">{t('polymarket.side')}</div>
      </div>

      {/* Trades List */}
      <div className="whale-trades-body" ref={scrollContainerRef}>
        {filteredTrades.length === 0 ? (
          <div className="no-trades">
            <p>{t('polymarket.noTrades')}</p>
          </div>
        ) : (
          filteredTrades.map((trade) => {
            const tradeKey = `${trade.transactionHash || trade.marketSlug}-${trade.timestamp}`;
            return (
              <TradeRow
                key={tradeKey}
                trade={trade}
                onClick={handleTradeClick}
                timeAgo={formatTimeAgo(trade.timestamp, t)}
                isLargestTrade={tradeKey === largestTradeKey}
                t={t}
              />
            );
          })
        )}
      </div>

      {/* Scroll to Top Button */}
      {!isAtTop && filteredTrades.length > 0 && (
        <button className="scroll-to-top-btn" onClick={scrollToTop}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M12 4l-8 8h5v8h6v-8h5z"/>
          </svg>
        </button>
      )}
    </div>
  );
}

export default PolymarketTradesTable;
